import React, { useCallback, useEffect, useState } from "react";
import { UserCog, Plus, ShieldCheck, Power } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Label, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const ROLE_LABEL = {
  master_admin: "Master Admin",
  platform_sales: "Sales",
  platform_ops: "Operations",
  platform_finance: "Finance",
};
const ROLE_COLOR = {
  master_admin: "bg-indigo-50 text-indigo-700",
  platform_sales: "bg-blue-50 text-blue-700",
  platform_ops: "bg-amber-50 text-amber-700",
  platform_finance: "bg-emerald-50 text-emerald-700",
};

export default function PlatformStaff() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "platform_sales" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/platform/staff").then((r) => setRows(r.data)).catch((e) => console.warn("Staff load failed", e)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/platform/staff", form);
      toast.success("Team member added");
      setShowNew(false);
      setForm({ name: "", email: "", password: "", role: "platform_sales" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/platform/staff/${s.id}/status`, { active: !s.active });
      toast.success(s.active ? "Access revoked" : "Access restored");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Servantage Team</h1></div>
        <Button onClick={() => setShowNew(true)} data-testid="new-staff-button" className="bg-[#4338CA] hover:bg-[#3730A3]">
          <Plus className="mr-1 h-4 w-4" /> Add team member
        </Button>
      </div>

      {rows.length === 0 ? <EmptyState icon={UserCog} title="No team members yet" /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`staff-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  {s.is_master ? <ShieldCheck className="h-5 w-5 text-indigo-600" /> : <UserCog className="h-5 w-5" />}
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${ROLE_COLOR[s.role] || "bg-slate-100 text-slate-600"}`}>
                  {ROLE_LABEL[s.role] || s.role}
                </span>
              </div>
              <p className="mt-3 font-head text-lg font-semibold text-slate-900">{s.name}</p>
              <p className="text-sm text-slate-400">{s.email}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className={`text-xs font-bold ${s.active ? "text-emerald-600" : "text-red-500"}`}>
                  {s.active ? "Active" : "Disabled"}
                </span>
                {!s.is_self && (
                  <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-staff-${s.id}`}
                    className={s.active ? "text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}>
                    <Power className="mr-1 h-3.5 w-3.5" /> {s.active ? "Revoke" : "Restore"}
                  </Button>
                )}
                {s.is_self && <span className="text-xs text-slate-400">You</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Temporary password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
              <p className="mt-1 text-xs text-slate-400">Minimum 8 characters — a fallback only. They'll get an email with a secure link to set their own password.</p>
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="master_admin">Master Admin</SelectItem>
                  <SelectItem value="platform_sales">Sales</SelectItem>
                  <SelectItem value="platform_ops">Operations</SelectItem>
                  <SelectItem value="platform_finance">Finance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-staff"
              disabled={saving || !form.name || !form.email || form.password.length < 8}
              className="bg-[#4338CA] hover:bg-[#3730A3]">Add member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
